if (!customElements.get('predictive-search')) {
class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
    this.setupSuggestedKeywords(); // 初始化推荐关键词点击
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    
    // 添加搜索按钮点击事件处理
    const searchButton = this.querySelector('[data-search-trigger]');
    if (searchButton) {
      searchButton.addEventListener('click', this.onSearchButtonClick.bind(this));
    }
  }

  // 静态推荐关键词点击
  setupSuggestedKeywords() {
    this.querySelectorAll('[data-predictive-keyword]').forEach(el => {
      // 阻止失焦
      el.addEventListener('mousedown', e => e.preventDefault());
      // 点击填充并搜索，不关闭弹窗
      el.addEventListener('click', e => {
        const keyword = e.currentTarget.dataset.predictiveKeyword;
        if (!keyword) return;
        this.input.value = keyword;
        this.getSearchResults(keyword);
        this.input.focus(); // 保持焦点，避免弹窗关闭
      });
    });
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    // Always prevent form submission to avoid page navigation
    event.preventDefault();
    
    const query = this.getQuery();
    if (query.length) {
      // Trigger predictive search if there's a search term
      this.getSearchResults(query);
    }
    
    return false;
  }

  onSearchButtonClick(event) {
    event.preventDefault(); // 阻止默认行为
    const query = this.getQuery();
    if (query.length) {
      // 触发预测搜索而不是跳转页面
      this.getSearchResults(query);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();
    if (!currentSearchTerm.length) return;
    if (this.searchTerm !== currentSearchTerm) this.onChange();
    else if (this.getAttribute('results') === 'true') this.open();
    else this.getSearchResults(this.searchTerm);
  }

  onFocusOut() {
    setTimeout(() => {
      // 弹窗内部点击不关闭
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp': this.switchOption('up'); break;
      case 'ArrowDown': this.switchOption('down'); break;
      case 'Enter': event.preventDefault(); this.getSearchResults(this.getQuery()); break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') event.preventDefault();
  }

  updateSearchForTerm(prev, next) {
    const el = this.querySelector('[data-predictive-search-search-for-text]');
    if (!el) return;
    const text = el.innerText;
    if (text.match(new RegExp(prev, 'g'))?.length > 1) return;
    el.innerText = text.replace(prev, next);
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;
    const moveUp = direction === 'up';
    const selected = this.querySelector('[aria-selected="true"]');
    const all = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(el => el.offsetParent !== null);
    let selectedIndex = all.indexOf(selected);
    let nextIndex = moveUp
      ? (selectedIndex <= 0 ? all.length - 1 : selectedIndex - 1)
      : (selectedIndex >= all.length - 1 ? 0 : selectedIndex + 1);
    if (nextIndex === selectedIndex) return;
    if (selected) selected.setAttribute('aria-selected', false);
    all[nextIndex].setAttribute('aria-selected', true);
    this.input.setAttribute('aria-activedescendant', all[nextIndex].id);
  }

  getSearchResults(searchTerm) {
    const key = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();
    if (this.cachedResults[key]) { this.renderSearchResults(this.cachedResults[key]); return; }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal
    })
      .then(r => r.ok ? r.text() : Promise.reject(r.status))
      .then(text => {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const sectionElement = doc.querySelector('#shopify-section-predictive-search');
        const resultsElement = sectionElement?.querySelector('#predictive-search-results');
        const markup = resultsElement ? resultsElement.outerHTML : sectionElement?.innerHTML || '';
        this.allPredictiveSearchInstances.forEach(instance => instance.cachedResults[key] = markup);
        this.renderSearchResults(markup);
      })
      .catch(err => {
        if (err?.code !== 20) this.close();
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');
    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(text) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = text;
    setTimeout(() => { this.statusElement.setAttribute('aria-hidden', 'true'); }, 1000);
  }

  renderSearchResults(markup) {
    this.predictiveSearchResults.innerHTML = markup;
    this.setAttribute('results', true);
    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const countEl = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (countEl) this.setLiveRegionText(countEl.textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight = window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) this.input.value = '';
    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', false);
    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
}

// Add input event listener to hide loading spinner when user types
document.addEventListener('DOMContentLoaded', function() {
  const searchInputs = document.querySelectorAll('input[type="search"]');
  searchInputs.forEach(input => {
    input.addEventListener('input', function() {
      const searchForm = this.closest('search-form') || this.closest('predictive-search');
      if (searchForm) {
        const predictiveSearchContainer = searchForm.querySelector('[data-predictive-search]');
        const suggestedSearchesContainer = searchForm.querySelector('.suggested_searches_text_container');
        
        if (this.value.trim() === '') {
          // Hide search results and show suggested searches
          if (predictiveSearchContainer) {
            predictiveSearchContainer.style.display = 'none';
          }
          if (suggestedSearchesContainer) {
            suggestedSearchesContainer.style.display = 'block';
          }
        }
      }
    });
  });

  // Prevent input losing focus when mousedown on suggested keyword (avoids drawer focusout close)
  document.addEventListener('mousedown', function(e) {
    if (e.target.closest('[data-predictive-keyword]')) {
      e.preventDefault();
    }
  });

  // Use event delegation for suggested search keywords (closest so clicking text/children works)
  document.addEventListener('click', function(e) {
    const keywordEl = e.target.closest('[data-predictive-keyword]');
    if (!keywordEl) return;

    e.preventDefault();
    const keywordText = keywordEl.getAttribute('data-predictive-keyword');
    if (!keywordText) return;

    // Find the search form and input
    const searchForm = keywordEl.closest('search-form') || keywordEl.closest('predictive-search');
    if (!searchForm) return;

    const searchInput = searchForm.querySelector('input[type="search"]');
    if (!searchInput) return;

    // Fill the search input with the keyword
    searchInput.value = keywordText;

    // Trigger search
    const predictiveSearchContainer = searchForm.querySelector('[data-predictive-search]');
    if (predictiveSearchContainer) {
      triggerPredictiveSearch(keywordText, predictiveSearchContainer);
    }
  });
});

// Global function to handle search button clicks
function handleSearchButtonClick(event) {
  event.preventDefault();
  event.stopPropagation();
  
  // Find the closest search form element (could be search-form or predictive-search)
  const searchFormElement = event.target.closest('search-form') || event.target.closest('predictive-search');
  if (!searchFormElement) return;
  
  // Get the search input value
  const searchInput = searchFormElement.querySelector('input[type="search"]');
  if (!searchInput) return;
  
  const searchTerm = searchInput.value.trim();
  if (!searchTerm) {
    // Hide predictive search if no search term
    const predictiveSearchContainer = searchFormElement.querySelector('[data-predictive-search]');
    if (predictiveSearchContainer) {
      predictiveSearchContainer.style.display = 'none';
    }
    return;
  }
  
  // If it's a predictive-search element, use its methods
  if (searchFormElement.tagName.toLowerCase() === 'predictive-search') {
    if (searchFormElement.getSearchResults) {
      searchFormElement.getSearchResults(searchTerm);
    } else {
      searchFormElement.onChange();
    }
  } else {
    // For search-form, we need to manually trigger predictive search
    // Find the predictive search container
    const predictiveSearchContainer = searchFormElement.querySelector('[data-predictive-search]');
    if (predictiveSearchContainer) {
      // Create a temporary predictive search instance or use global function
      triggerPredictiveSearch(searchTerm, predictiveSearchContainer);
    }
  }
}

// Global function to handle form submission
function handleFormSubmit(event) {
  // Always prevent form submission to avoid page navigation
  event.preventDefault();
  
  const form = event.target;
  const searchInput = form.querySelector('input[type="search"]');
  
  if (searchInput && searchInput.value.trim()) {
    // Find the predictive search container and trigger search
    const predictiveSearchContainer = form.querySelector('[data-predictive-search]');
    if (predictiveSearchContainer) {
      triggerPredictiveSearch(searchInput.value.trim(), predictiveSearchContainer);
    }
  }
  
  return false;
}

// Function to trigger predictive search manually
function triggerPredictiveSearch(searchTerm, container) {
  if (!searchTerm || !container) return;
  
  // Show loading state
  container.innerHTML = '<div class="predictive-search__loading-state">Loading...</div>';
  container.style.display = 'block';
  
  // Update status element with loading state
  const searchForm = container.closest('search-form') || container.closest('predictive-search');
  if (searchForm) {
    const statusElement = searchForm.querySelector('.predictive-search-status');
    if (statusElement) {
      statusElement.setAttribute('aria-hidden', 'false');
      statusElement.textContent = 'Loading...';
    }
  }
  
  // Fetch search results
  fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`)
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      return response.text();
    })
    .then(text => {
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const sectionElement = doc.querySelector('#shopify-section-predictive-search');
      
      if (!sectionElement) {
        throw new Error('No search results section found');
      }
      
      // Get the results markup
      const resultsElement = sectionElement.querySelector('#predictive-search-results');
      let markup = resultsElement ? resultsElement.outerHTML : sectionElement.innerHTML;
      
      if (!markup || markup.trim() === '') {
        throw new Error('No search results content');
      }
      
      // Remove any loading spinner from the markup
      markup = markup.replace(/<div class="predictive-search__loading-state">[\s\S]*?<\/div>/g, '');
      markup = markup.replace(/<div class="spinner">[\s\S]*?<\/div>/g, '');
      markup = markup.replace(/<svg[\s\S]*?class="spinner"[\s\S]*?<\/svg>/g, '');
      
      // Parse the markup to extract only product results
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = markup;
      
      // Find the products section
      const productsSection = tempDiv.querySelector('#predictive-search-results-products-list');
      let productCount = 0;
      
      if (productsSection) {
        // Count the number of products
        const productItems = productsSection.querySelectorAll('li');
        productCount = productItems.length;
        
        // Create a new results structure with only products
        const newMarkup = `
          <div id="predictive-search-results" role="listbox">
            <div id="predictive-search-results-groups-wrapper" class="predictive-search__results-groups-wrapper">
              <div class="predictive-search__result-group">
                <div>
                  <h2 id="predictive-search-products" class="predictive-search__heading xbloom-text-14 text-body caption-with-letter-spacing">${productCount} Search results</h2>
                  ${productsSection.outerHTML}
                </div>
              </div>
            </div>
          </div>
          <span class="hidden" data-predictive-search-live-region-count-value>
            ${productCount} search results found
          </span>
        `;
        container.innerHTML = newMarkup;
      } else {
        // If no products found, show no results message with SVG
        container.innerHTML = `
          <div class="predictive-search__no-results">
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" style="margin-bottom: 16px;">
              <circle cx="32" cy="32" r="31.4" stroke="black" stroke-opacity="0.05" stroke-width="1.2"/>
              <path d="M37.0557 29.7021C37.9957 29.6547 38.92 29.9578 39.6494 30.5527C40.3788 31.1477 40.8616 31.9924 41.0039 32.9229C41.144 33.8399 40.9424 34.7757 40.4404 35.5547L42.3154 37.4297C42.3904 37.5041 42.4506 37.5929 42.4912 37.6904C42.5318 37.7879 42.5527 37.8925 42.5527 37.998C42.5527 38.1036 42.5318 38.2082 42.4912 38.3057C42.4506 38.4031 42.3903 38.4911 42.3154 38.5654C42.2412 38.6397 42.1536 38.699 42.0566 38.7393C41.9595 38.7795 41.8551 38.8008 41.75 38.8008C41.6449 38.8008 41.5405 38.7795 41.4434 38.7393C41.3464 38.699 41.2578 38.6407 41.1836 38.5664L39.3047 36.6904C38.5257 37.1924 37.5899 37.394 36.6729 37.2539C35.7424 37.1116 34.8977 36.6288 34.3027 35.8994C33.7078 35.17 33.4047 34.2457 33.4521 33.3057C33.4997 32.3654 33.8949 31.4762 34.5605 30.8105C35.2262 30.1449 36.1154 29.7497 37.0557 29.7021ZM32 37.2002C32.2122 37.2002 32.4154 37.2845 32.5654 37.4346C32.7155 37.5846 32.7998 37.7878 32.7998 38C32.7998 38.2122 32.7155 38.4154 32.5654 38.5654C32.4154 38.7155 32.2122 38.7998 32 38.7998H23.75C23.5378 38.7998 23.3346 38.7155 23.1846 38.5654C23.0345 38.4154 22.9502 38.2122 22.9502 38C22.9502 37.7878 23.0345 37.5846 23.1846 37.4346C23.3346 37.2845 23.5378 37.2002 23.75 37.2002H32ZM37.6787 31.3418C37.2521 31.2571 36.81 31.3013 36.4082 31.4678C36.0063 31.6342 35.6626 31.9157 35.4209 32.2773C35.1792 32.6391 35.0498 33.0649 35.0498 33.5C35.0498 34.0835 35.2818 34.6431 35.6943 35.0557C36.1069 35.4682 36.6665 35.7002 37.25 35.7002C37.6851 35.7002 38.1109 35.5708 38.4727 35.3291C38.8343 35.0874 39.1158 34.7437 39.2822 34.3418C39.4487 33.9398 39.4931 33.4971 39.4082 33.0703C39.3233 32.6437 39.1132 32.2519 38.8057 31.9443C38.498 31.6367 38.1055 31.4267 37.6787 31.3418ZM30.5 31.2002C30.7122 31.2002 30.9154 31.2845 31.0654 31.4346C31.2155 31.5846 31.2998 31.7878 31.2998 32C31.2998 32.2122 31.2155 32.4154 31.0654 32.5654C30.9154 32.7155 30.7122 32.7998 30.5 32.7998H23.75C23.5378 32.7998 23.3346 32.7155 23.1846 32.5654C23.0345 32.4154 22.9502 32.2122 22.9502 32C22.9502 31.7878 23.0345 31.5846 23.1846 31.4346C23.3346 31.2845 23.5378 31.2002 23.75 31.2002H30.5ZM40.25 25.2002C40.4622 25.2002 40.6654 25.2845 40.8154 25.4346C40.9655 25.5846 41.0498 25.7878 41.0498 26C41.0498 26.2122 40.9655 26.4154 40.8154 26.5654C40.6654 26.7155 40.4622 26.7998 40.25 26.7998H23.75C23.5378 26.7998 23.3346 26.7155 23.1846 26.5654C23.0345 26.4154 22.9502 26.2122 22.9502 26C22.9502 25.7878 23.0345 25.5846 23.1846 25.4346C23.3346 25.2845 23.5378 25.2002 23.75 25.2002H40.25Z" fill="black" stroke="black" stroke-width="0.1"/>
            </svg>
            <div class="predictive-search__no-results-text xbloom-text-14">No results found</div>
          </div>
        `;
      }
      
      // Show the results
      container.style.display = 'block';
      
      // Update status element with results count
      const searchForm = container.closest('search-form') || container.closest('predictive-search');
      if (searchForm) {
        const statusElement = searchForm.querySelector('.predictive-search-status');
        if (statusElement) {
          statusElement.setAttribute('aria-hidden', 'false');
          if (productCount > 0) {
            statusElement.textContent = `${productCount} search results found`;
          } else {
            statusElement.textContent = 'No results found';
          }
          setTimeout(() => {
            statusElement.setAttribute('aria-hidden', 'true');
          }, 1000);
        }
        
        // Hide suggested searches when showing results
        const suggestedSearchesContainer = searchForm.querySelector('.suggested_searches_text_container');
        if (suggestedSearchesContainer) {
          suggestedSearchesContainer.style.display = 'none';
        }
      }
      
      // Remove loading class if it exists
      container.classList.remove('loading');
    })
    .catch(err => {
      console.error('Search error:', err);
      container.innerHTML = `
        <div class="predictive-search__no-results" style="text-align: center; padding: 40px 20px;">
          <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" style="margin-bottom: 16px;">
            <circle cx="32" cy="32" r="31.4" stroke="black" stroke-opacity="0.05" stroke-width="1.2"/>
            <path d="M37.0557 29.7021C37.9957 29.6547 38.92 29.9578 39.6494 30.5527C40.3788 31.1477 40.8616 31.9924 41.0039 32.9229C41.144 33.8399 40.9424 34.7757 40.4404 35.5547L42.3154 37.4297C42.3904 37.5041 42.4506 37.5929 42.4912 37.6904C42.5318 37.7879 42.5527 37.8925 42.5527 37.998C42.5527 38.1036 42.5318 38.2082 42.4912 38.3057C42.4506 38.4031 42.3903 38.4911 42.3154 38.5654C42.2412 38.6397 42.1536 38.699 42.0566 38.7393C41.9595 38.7795 41.8551 38.8008 41.75 38.8008C41.6449 38.8008 41.5405 38.7795 41.4434 38.7393C41.3464 38.699 41.2578 38.6407 41.1836 38.5664L39.3047 36.6904C38.5257 37.1924 37.5899 37.394 36.6729 37.2539C35.7424 37.1116 34.8977 36.6288 34.3027 35.8994C33.7078 35.17 33.4047 34.2457 33.4521 33.3057C33.4997 32.3654 33.8949 31.4762 34.5605 30.8105C35.2262 30.1449 36.1154 29.7497 37.0557 29.7021ZM32 37.2002C32.2122 37.2002 32.4154 37.2845 32.5654 37.4346C32.7155 37.5846 32.7998 37.7878 32.7998 38C32.7998 38.2122 32.7155 38.4154 32.5654 38.5654C32.4154 38.7155 32.2122 38.7998 32 38.7998H23.75C23.5378 38.7998 23.3346 38.7155 23.1846 38.5654C23.0345 38.4154 22.9502 38.2122 22.9502 38C22.9502 37.7878 23.0345 37.5846 23.1846 37.4346C23.3346 37.2845 23.5378 37.2002 23.75 37.2002H32ZM37.6787 31.3418C37.2521 31.2571 36.81 31.3013 36.4082 31.4678C36.0063 31.6342 35.6626 31.9157 35.4209 32.2773C35.1792 32.6391 35.0498 33.0649 35.0498 33.5C35.0498 34.0835 35.2818 34.6431 35.6943 35.0557C36.1069 35.4682 36.6665 35.7002 37.25 35.7002C37.6851 35.7002 38.1109 35.5708 38.4727 35.3291C38.8343 35.0874 39.1158 34.7437 39.2822 34.3418C39.4487 33.9398 39.4931 33.4971 39.4082 33.0703C39.3233 32.6437 39.1132 32.2519 38.8057 31.9443C38.498 31.6367 38.1055 31.4267 37.6787 31.3418ZM30.5 31.2002C30.7122 31.2002 30.9154 31.2845 31.0654 31.4346C31.2155 31.5846 31.2998 31.7878 31.2998 32C31.2998 32.2122 31.2155 32.4154 31.0654 32.5654C30.9154 32.7155 30.7122 32.7998 30.5 32.7998H23.75C23.5378 32.7998 23.3346 32.7155 23.1846 32.5654C23.0345 32.4154 22.9502 32.2122 22.9502 32C22.9502 31.7878 23.0345 31.5846 23.1846 31.4346C23.3346 31.2845 23.5378 31.2002 23.75 31.2002H30.5ZM40.25 25.2002C40.4622 25.2002 40.6654 25.2845 40.8154 25.4346C40.9655 25.5846 41.0498 25.7878 41.0498 26C41.0498 26.2122 40.9655 26.4154 40.8154 26.5654C40.6654 26.7155 40.4622 26.7998 40.25 26.7998H23.75C23.5378 26.7998 23.3346 26.7155 23.1846 26.5654C23.0345 26.4154 22.9502 26.2122 22.9502 26C22.9502 25.7878 23.0345 25.5846 23.1846 25.4346C23.3346 25.2845 23.5378 25.2002 23.75 25.2002H40.25Z" fill="black" stroke="black" stroke-width="0.1"/>
          </svg>
          <div style="font-size: 16px; color: #666;">No results found</div>
        </div>
      `;
      container.style.display = 'block';
    });
}
